// RigSentinel — RC-1 Kill Sheet architecture (Phase 1)
//
// SINGLE source of truth for "what is the active Engineering Context for
// a Kill Sheet on a given well?".
//
// Every Kill Sheet code path — create, synchronize, re-open — calls this
// one function and nothing else. There are NO other context builders, no
// inline `rceCtx` assemblies, no per-module ad-hoc fallbacks scattered
// across the editor or storage layer. If you add a new module to the
// active-state precedence chain, you change ONE file: this one.
//
// Purity:
//   * Pure read. No localStorage writes.
//   * No events emitted.
//   * Trivial to unit-test.
//
// Precedence per active id (highest → lowest):
//   sessionOverride > well.active* > in-use/first-of-kind for this well
//   > sole-of-kind in the workspace > "".

import { getWellProfile }            from "@/lib/well/storage";
import { listMudProgramsForWell }    from "@/lib/mud/program";
import { listRigSheets }             from "@/lib/rigsheet/storage";
import { buildKillSheetContextSnapshot } from "@/lib/killsheet/storage";

const _nonEmpty = (v) => v !== "" && v !== null && v !== undefined;

/**
 * Resolve the active ids for every module that contributes to a Kill
 * Sheet. Read-only walk through Well / Mud / Rig storage.
 *
 *  @param {{
 *    projectId?: string, rigId?: string, wellId?: string,
 *    bhaId?: string, drillStringId?: string, mudProgramId?: string,
 *  }} sessionOverrides
 *  @param {string} wellId
 *  @returns {{
 *    projectId: string, rigId: string, wellId: string,
 *    bhaId: string, drillStringId: string, mudProgramId: string,
 *  }}
 */
export function resolveActiveIds(sessionOverrides = {}, wellId = "") {
  const so = sessionOverrides || {};
  const effectiveWellId = _nonEmpty(so.wellId) ? so.wellId : (wellId || "");
  let well = null;
  try { well = effectiveWellId ? getWellProfile(effectiveWellId) : null; }
  catch { well = null; }

  // BHA: session > well.activeBhaId > "".
  let bhaId =
    _nonEmpty(so.bhaId)         ? so.bhaId
    : (well && _nonEmpty(well.activeBhaId)) ? well.activeBhaId
    : "";

  // Drill String: session > well.activeDrillStringId > "".
  let drillStringId =
    _nonEmpty(so.drillStringId) ? so.drillStringId
    : (well && _nonEmpty(well.activeDrillStringId)) ? well.activeDrillStringId
    : "";

  // Mud Program: session > in-use/first-for-well > "".
  let mudProgramId = _nonEmpty(so.mudProgramId) ? so.mudProgramId : "";
  if (!mudProgramId && effectiveWellId) {
    try {
      const candidates = listMudProgramsForWell(effectiveWellId) || [];
      const inUse = candidates.find((p) => p.status === "in-use");
      mudProgramId = (inUse || candidates[0] || {}).id || "";
    } catch { /* never block context construction */ }
  }

  // Rig: session > sole-of-kind in workspace > "".
  let rigId = _nonEmpty(so.rigId) ? so.rigId : "";
  if (!rigId) {
    try {
      const all = listRigSheets() || [];
      if (all.length === 1) rigId = all[0].id || "";
    } catch { /* never block context construction */ }
  }

  return {
    projectId:     _nonEmpty(so.projectId) ? so.projectId : "",
    rigId,
    wellId:        effectiveWellId,
    bhaId,
    drillStringId,
    mudProgramId,
  };
}

/**
 * Build the complete KickState for a Kill Sheet:
 *   * `ids`        — resolved active ids (see resolveActiveIds)
 *   * `snapshot`   — frozen Engineering Snapshot (verbatim shape of
 *                    contextSnapshot — what the audit trail records)
 *   * `confidence` — per-RCE-path confidence map (mirror of snapshot.confidence)
 *
 * Internally composes `resolveActiveIds` and `buildKillSheetContextSnapshot`
 * so every Kill Sheet caller uses exactly the same two steps.
 *
 * Pure. Safe to call on every render.
 *
 *  @param {object} session  — current Engineering Session (or null).
 *  @param {string} wellId   — URL-scoped wellId (fallback when session.wellId is empty).
 */
export function buildKickState(session, wellId) {
  const ids = resolveActiveIds(session || {}, wellId);
  const snapshot = buildKillSheetContextSnapshot(ids);
  return {
    ids,
    snapshot,
    confidence: (snapshot && snapshot.confidence) || {},
  };
}
